import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('armazem88_cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('armazem88_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, maxStock = null) => {
    const stock = maxStock !== null ? maxStock : (product.stock || 999);
    
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        if (existing.quantity >= stock) {
          toast.warning(`Estoque máximo: ${stock} unidades`);
          return prev;
        }
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1, maxStock: stock }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1, maxStock: stock }];
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity, maxStock = null) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    const item = cartItems.find(i => i.id === productId);
    const stock = maxStock !== null ? maxStock : (item?.maxStock || item?.stock || 999);
    
    if (quantity > stock) {
      toast.warning(`Estoque máximo disponível: ${stock} unidades`);
      quantity = stock;
    }
    
    setCartItems(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity, maxStock: stock } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.discount ? item.price * (1 - item.discount / 100) : item.price;
      return total + price * item.quantity;
    }, 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const generateWhatsAppMessage = (address = '', orderNumber = '') => {
    const WHATSAPP_NUMBER = '5588988654624';
    const PIX_KEY = '88988654624';
    let message = 'Olá, Armazém 88! 🛒 ';
    
    if (orderNumber) {
      message += `Pedido *${orderNumber}*\n\n`;
    } else {
      message += 'Gostaria de fazer um pedido:\n\n';
    }
    
    cartItems.forEach(item => {
      const price = item.discount ? item.price * (1 - item.discount / 100) : item.price;
      message += `• ${item.quantity}x ${item.name} - R$ ${(price * item.quantity).toFixed(2)}\n`;
    });
    
    message += `\n💰 *Total: R$ ${getTotal().toFixed(2)}*`;
    
    if (address) {
      message += `\n\n📍 *Endereço de entrega:*\n${address}`;
    }
    
    message += `\n\n💳 *Formas de Pagamento:*`;
    message += `\n• PIX: ${PIX_KEY}`;
    message += `\n• Cartão (Débito/Crédito)`;
    message += `\n• Dinheiro`;
    
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotal,
        getTotalItems,
        generateWhatsAppMessage,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
